import React from "react";
import {
  Form,
  ModalBody,
  Label,
  FormFeedback,
  FormText,
  Input,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import * as moment from "moment";

import SelectContractor from "pages/Contracts/SelectContractor";
import SelectLTAType from "./SelectLTAType";
import SelectLTAService from "./SelectLTAService";
import SelectLeadAgency from "./SelectLeadAgency";
import SelectAgencies from "./SelectAgencies";
import SelectLTADocuments from "./SelectLTADocuments";
import SelectRegion from "pages/Contracts/SelectRegions";

/** A labelled divider that breaks the long form into readable sections. */
const Section = ({
  title,
  subtitle,
  first,
}: {
  title: string;
  subtitle?: string;
  /** The opening section sits flush with the top of the modal body. */
  first?: boolean;
}) => (
  <div className={`mb-3 ${first ? "" : "mt-4"}`}>
    <h6 className="mb-1 fs-15">{title}</h6>
    {subtitle && <p className="text-muted mb-0 fs-13">{subtitle}</p>}
  </div>
);

const LTAForm: React.FC<{
  toggle: () => void;
  loading: boolean;
  isUpdate?: boolean;
  validation: any;
  /** Agency users do not pick a lead agency — the backend sets it to theirs. */
  hideLeadAgency?: boolean;
}> = ({ validation, toggle, isUpdate, loading, hideLeadAgency }) => {
  const { startDate, endDate } = validation.values;

  // A plain-language read-out of the term, so the user can sanity-check the
  // two date pickers without doing the arithmetic themselves.
  const termSummary = React.useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid()) return null;
    if (end.isBefore(start)) return { invalid: true, text: "" };
    const months = end.diff(start, "months");
    const days = end.diff(start, "days");
    return {
      invalid: false,
      text:
        months >= 1
          ? `${months} month${months === 1 ? "" : "s"} (${days} days)`
          : `${days} day${days === 1 ? "" : "s"}`,
    };
  }, [startDate, endDate]);

  return (
    <Form
      className="tablelist-form"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <ModalBody>
        <Section
          first
          title="Agreement Details"
          subtitle="How this agreement is identified and classified"
        />

        <Row>
          <Col md={8} className="mb-3">
            <Label htmlFor="title" className="form-label">
              LTA Title
            </Label>
            <Input
              name="title"
              id="title"
              className="form-control"
              placeholder="e.g. Long Term Agreement for Fuel Supply"
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.title || ""}
              invalid={
                validation.touched.title && validation.errors.title
                  ? true
                  : false
              }
            />
            {validation.touched.title && validation.errors.title ? (
              <FormFeedback type="invalid">
                {validation.errors.title}
              </FormFeedback>
            ) : null}
          </Col>
          <Col md={4} className="mb-3">
            <Label htmlFor="ltaNumber" className="form-label">
              LTA Number
            </Label>
            <Input
              name="ltaNumber"
              id="ltaNumber"
              className="form-control"
              placeholder="e.g. LTA-2026-014"
              type="text"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.ltaNumber || ""}
              invalid={
                validation.touched.ltaNumber && validation.errors.ltaNumber
                  ? true
                  : false
              }
            />
            {validation.touched.ltaNumber && validation.errors.ltaNumber ? (
              <FormFeedback type="invalid">
                {validation.errors.ltaNumber}
              </FormFeedback>
            ) : null}
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <SelectLTAType validation={validation} />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="ltaValue" className="form-label">
              LTA Value (USD)
            </Label>
            <Input
              name="ltaValue"
              id="ltaValue"
              className="form-control"
              placeholder="e.g. 2000000"
              type="number"
              min={0}
              step="any"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.ltaValue ?? ""}
              invalid={
                validation.touched.ltaValue && validation.errors.ltaValue
                  ? true
                  : false
              }
            />
            {validation.touched.ltaValue && validation.errors.ltaValue ? (
              <FormFeedback type="invalid">
                {validation.errors.ltaValue}
              </FormFeedback>
            ) : null}
          </Col>
        </Row>

        <Section
          title="Agreement Term"
          subtitle="The period the agreement runs for"
        />

        <Row>
          <Col md={6} className="mb-3">
            <Label htmlFor="startDate-field" className="form-label">
              LTA Start Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              name="startDate"
              id="startDate-field"
              className="form-control"
              placeholder="Select start date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(e) => {
                validation.setFieldValue("startDate", e[0]);
              }}
              value={validation.values.startDate || ""}
            />
          </Col>
          <Col md={6} className="mb-3">
            <Label htmlFor="endDate-field" className="form-label">
              LTA End Date
            </Label>
            {/* @ts-ignore */}
            <Flatpickr
              name="endDate"
              id="endDate-field"
              className="form-control"
              placeholder="Select end date"
              options={{
                enableTime: false,
                altInput: false,
                altFormat: "d M, Y",
                dateFormat: "d M, Y",
              }}
              onChange={(e) => {
                validation.setFieldValue("endDate", e[0]);
              }}
              value={validation.values.endDate || ""}
            />
            {validation.errors.endDate ? (
              <div className="text-danger fs-12 mt-1">
                {validation.errors.endDate}
              </div>
            ) : null}
          </Col>
          {termSummary && (
            <Col md={12} className="mb-3">
              {termSummary.invalid ? (
                <div className="alert alert-danger py-2 mb-0 fs-13">
                  <i className="ri-error-warning-line align-bottom me-1"></i>
                  The end date falls before the start date.
                </div>
              ) : (
                <div className="alert alert-info py-2 mb-0 fs-13">
                  <i className="ri-time-line align-bottom me-1"></i>
                  Agreement term: <strong>{termSummary.text}</strong>
                </div>
              )}
            </Col>
          )}
        </Row>

        <Section
          title="Parties"
          subtitle="The contractor and the UN agencies involved"
        />

        <Row>
          <Col md={12}>
            <SelectContractor validation={validation} />
          </Col>
          {!hideLeadAgency && (
            <Col md={6} className="mb-3">
              <SelectLeadAgency validation={validation} />
            </Col>
          )}
          <Col md={hideLeadAgency ? 12 : 6} className="mb-3">
            <div className="form-check form-switch form-switch-lg px-0 form-check-right mt-lg-4 pt-lg-2">
              <Input
                name="isUsableByOtherAgencies"
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="isUsableByOtherAgencies"
                onChange={validation.handleChange}
                checked={!!validation.values.isUsableByOtherAgencies}
              />
              <Label
                className="form-check-label"
                htmlFor="isUsableByOtherAgencies"
              >
                LTA usable by other UN agencies
              </Label>
            </div>
          </Col>
          <Col md={12} className="mb-3">
            <SelectAgencies
              validation={validation}
              helpText="The UN agencies currently drawing on this agreement."
            />
          </Col>
        </Row>

        <Section
          title="Scope"
          subtitle="What the agreement covers"
        />

        <Row>
          <Col md={12} className="mb-3">
            <SelectLTAService validation={validation} />
          </Col>
          <Col md={12}>
            <SelectRegion validation={validation} />
          </Col>
        </Row>

        <Section
          title="Documents & Notes"
          subtitle="Signed agreement and any supporting context"
        />

        <Row>
          <Col md={12} className="mb-3">
            <SelectLTADocuments validation={validation} />
          </Col>
          <Col md={12} className="mb-3">
            <Label htmlFor="additionalInformation" className="form-label">
              Additional Information
            </Label>
            <Input
              name="additionalInformation"
              id="additionalInformation"
              className="form-control"
              placeholder="Renewal terms, pricing arrangements, conditions of use..."
              type="textarea"
              rows={4}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.additionalInformation || ""}
            />
            <FormText>
              Visible to all UN agencies that can view this agreement.
            </FormText>
          </Col>
        </Row>
      </ModalBody>

      <div className="modal-footer">
        <div className="hstack gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => {
              toggle();
            }}
          >
            Close
          </button>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading && (
              <Spinner
                color="light"
                size="sm"
                style={{ marginRight: "8px", marginBottom: "-1px" }}
              ></Spinner>
            )}
            {isUpdate ? "Update LTA" : "Add LTA"}
          </button>
        </div>
      </div>
    </Form>
  );
};

export default LTAForm;
